import { ChatBubbleLeftRightIcon, UserGroupIcon } from '@heroicons/react/24/outline'
import { ReactElement } from 'react'
import { OpsTrendGraph, type TrendSeries } from 'app/components/ai-agent-voice-ops/OpsTrendGraph'
import { SectionTitle } from 'app/components/ai-agent-voice-ops/SectionTitle'
import SimpleTable from 'app/components/table/SimpleTable'
import { TableEmptyState } from 'app/components/ui/table-empty-state'
import SegmentControl from 'app/components/viz/SegmentControl'
import { EMPTY_DAILY_TOTALS, type SmsDailyField } from './smsAgentOpsMetrics'
import {
  LOW_VOLUME_FLOOR,
  TAKEOVER_CAUSE_LABEL,
  TAKEOVER_CAUSES,
  TAKEOVER_CRITICAL_AT,
  TAKEOVER_WARNING_AT,
} from './smsTakeovers'
import { DataSourceBadge } from './DataSourceBadge'
import { ListErrorState } from './ListErrorState'
import type { TakeoverCauseFilter, UseSmsAgentOpsReturn } from './useSmsAgentOps'

const TAKEOVER_SERIES: TrendSeries<SmsDailyField>[] = [
  { key: TAKEOVER_CAUSE_LABEL.staffReply, color: `green`, value: (totals) => totals.staffReplyTakeovers },
  { key: TAKEOVER_CAUSE_LABEL.manualToggle, color: `amber`, value: (totals) => totals.manualToggleTakeovers },
  { key: TAKEOVER_CAUSE_LABEL.optOut, color: `red`, value: (totals) => totals.optOutTakeovers },
]

const CAUSE_FILTER_OPTIONS: { value: TakeoverCauseFilter; label: string }[] = [
  { value: `all`, label: `All` },
  ...TAKEOVER_CAUSES.map((cause) => ({ value: cause, label: TAKEOVER_CAUSE_LABEL[cause] })),
]

type TakeoversSectionProps = Pick<
  UseSmsAgentOpsReturn,
  | `listErrors`
  | `fromDate`
  | `toDate`
  | `isLoading`
  | `summary`
  | `daily`
  | `leaderboardRows`
  | `takeoverColumns`
  | `staffRows`
  | `staffColumns`
  | `visibleTakeovers`
  | `takeoverCounts`
  | `takeoverCauseFilter`
  | `setTakeoverCauseFilter`
  | `takeoverListColumns`
  | `takeoverListRef`
>

/**
 * Who takes conversations away from the AI (plan D8–D11): takeovers over time by cause,
 * SMS Agent locations worst first, the staff behind them, and the live takeover list.
 */
export function TakeoversSection({
  listErrors,
  fromDate,
  toDate,
  isLoading,
  summary,
  daily,
  leaderboardRows,
  takeoverColumns,
  staffRows,
  staffColumns,
  visibleTakeovers,
  takeoverCounts,
  takeoverCauseFilter,
  setTakeoverCauseFilter,
  takeoverListColumns,
  takeoverListRef,
}: TakeoversSectionProps): ReactElement {
  return (
    <>
      <OpsTrendGraph
        title="Takeovers"
        adornment={<DataSourceBadge source="preview" />}
        badge={`${summary.takeovers.toLocaleString(`en-US`)} Takeovers`}
        series={TAKEOVER_SERIES}
        daily={daily}
        emptyTotals={EMPTY_DAILY_TOTALS}
        fromDate={fromDate}
        toDate={toDate}
        isLoading={isLoading}
      />

      <div className="mb-8">
        <div className="mb-1">
          <SectionTitle
            title="Takeovers by Location"
            badge={`${leaderboardRows?.length ?? 0} Locations`}
            adornment={<DataSourceBadge source="preview" />}
          />
        </div>
        <p className="mb-3 text-body-sm text-subtle">
          {`SMS Agent locations, highest takeover rate first. ${TAKEOVER_CRITICAL_AT}% or more is critical and ${TAKEOVER_WARNING_AT}% or more a warning; under ${LOW_VOLUME_FLOOR} AI conversations is too few to rate and sorts last.`}
        </p>
        <SimpleTable
          activeSubpage="sms-agent-ops-takeovers"
          data={leaderboardRows}
          columns={takeoverColumns}
          enableDeepLinking={false}
          hideFooter
          emptyState={
            <TableEmptyState
              icon={<ChatBubbleLeftRightIcon className="h-6 w-6" />}
              title="No AI conversations"
              description="SMS Agent locations show up here once the AI agent has texted in the date range."
            />
          }
        />
      </div>

      <div className="mb-8">
        <div className="mb-1">
          <SectionTitle
            title="Who's Taking Over"
            badge={`${staffRows?.length ?? 0} Staff`}
            adornment={<DataSourceBadge source="preview" />}
          />
        </div>
        <p className="mb-3 text-body-sm text-subtle">
          Staff who replied on an AI thread or turned the AI off, most takeovers first.
        </p>
        <SimpleTable
          activeSubpage="sms-agent-ops-staff"
          data={staffRows}
          columns={staffColumns}
          enableDeepLinking={false}
          hideFooter
          emptyState={
            <TableEmptyState
              icon={<UserGroupIcon className="h-6 w-6" />}
              title="No staff takeovers"
              description="Nobody took a conversation away from the AI in the date range."
            />
          }
        />
      </div>

      <div ref={takeoverListRef} className="mb-8 scroll-mt-4">
        <div className="mb-1 flex flex-wrap items-center justify-between gap-y-2">
          <SectionTitle
            title="Takeover Log"
            badge={`${takeoverCounts.all} Takeovers`}
            adornment={<DataSourceBadge source="live" />}
          />
          {/* Scrolls on its own on a phone rather than pushing the page sideways. */}
          <div className="max-w-full overflow-x-auto">
            <SegmentControl
              label="Cause"
              buttons={CAUSE_FILTER_OPTIONS.map((option) => ({
                label: `${option.label} ${takeoverCounts[option.value]}`,
                onClick: () => setTakeoverCauseFilter(option.value),
                isActive: takeoverCauseFilter === option.value,
              }))}
            />
          </div>
        </div>
        <p className="mb-3 text-body-sm text-subtle">
          Each time a thread left the AI agent. Recorded since Sep 1, 2026.
        </p>
        <SimpleTable
          activeSubpage="sms-agent-ops-takeover-log"
          data={visibleTakeovers}
          columns={takeoverListColumns}
          enableDeepLinking={false}
          hideFooter
          emptyState={
            listErrors.takeovers ? (
              <ListErrorState subject="takeovers" />
            ) : (
              <TableEmptyState
                icon={<ChatBubbleLeftRightIcon className="h-6 w-6" />}
                title="No takeovers"
                description="Threads that left the AI agent in the date range show up here."
              />
            )
          }
        />
      </div>
    </>
  )
}
